import { Component, inject, OnDestroy, OnInit, ChangeDetectionStrategy } from '@angular/core';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { FileTypeDto } from '@coin/importer/dto/util';
import { ConfirmationDialogComponent, TurbineComponent, DropdownComponent } from '@coin/importer/core/feature';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { MatSelectChange } from '@angular/material/select';
import { FileTypeService } from '../../services/file-type/file-type.service';
import { ToastService } from '../../services/toast/toast.service';
import { V2ButtonComponent } from '@coin/shared/core/ui-storybook';

@Component({
  selector: 'ci-trigger-import-run-popup',
  templateUrl: './trigger-import-run-popup.component.html',
  styleUrls: ['./trigger-import-run-popup.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [TurbineComponent, DropdownComponent, V2ButtonComponent]
})
export class TriggerImportRunPopupComponent implements OnInit, OnDestroy {
  matDialogRef = inject<MatDialogRef<TriggerImportRunPopupComponent>>(MatDialogRef);
  private readonly dialog = inject(MatDialog);
  private readonly fileTypeService = inject(FileTypeService);
  private readonly toast = inject(ToastService);

  entities: FileTypeDto[] = [];
  selectedFileType: FileTypeDto;
  isLoading = false;
  private readonly destroy$: Subject<void> = new Subject<void>();

  ngOnInit(): void {
    this.fileTypeService.fileTypeSubject$.pipe(takeUntil(this.destroy$)).subscribe(fileTypes => {
      this.entities = fileTypes.filter(ft => !!ft.lambdaFunctionArn);
    });
  }

  applySelectionChange(event: MatSelectChange) {
    this.selectedFileType = event.value as FileTypeDto;
  }

  dropdownDisplayFn(entity: FileTypeDto): string {
    return entity.displayedName;
  }

  close(reload = false) {
    this.matDialogRef.close(reload);
  }

  openConfirmation() {
    this.dialog
      .open(ConfirmationDialogComponent, {
        disableClose: true,
        data: {
          headline: 'Trigger Import Run',
          msg: `This will trigger an import run for "${this.selectedFileType.displayedName}". Are you sure?`,
          cancelMsg: 'Cancel',
          confirmMsg: 'Yes - Trigger Import Run'
        }
      })
      .afterClosed()
      .pipe(takeUntil(this.destroy$))
      .subscribe(result => {
        if (!result) return;
        this.triggerImportRun();
      });
  }

  private triggerImportRun() {
    this.isLoading = true;
    this.fileTypeService
      .triggerImportRun(this.selectedFileType.prefix)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: () => {
          this.toast.success(
            `Import run for "${this.selectedFileType.displayedName}" has been triggered successfully.`
          );
          this.close(true);
        },
        error: () => {
          this.isLoading = false;
        }
      });
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
  }
}
